<script setup>
import { inject, ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import IndexFiltersModal from './IndexFiltersModal.vue'

import API from '@/js/api'

const emit = defineEmits(['update'])
const { t, locale } = useI18n()
const api = inject('api')
const model = defineModel()
const active = ref({ modal: false })
const filters = ref(null)
const initialValue = {
  type: [],
  date: [],
  favorites: '0',
  view: 'index',
}

const formatDay = (dateString) => {
  const date = new Date(dateString)
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  return `${day}/${month}`
}

const setDate = async (date) => {
  if (model.value.date == date) model.value.date = []
  else model.value.date = date

  filters.value = await API.get(api.filters + API.getQS(model.value))
}

const resetModel = () => {
  model.value = initialValue
}

const removeType = (index) => {
  model.value.type.splice(index, 1)
}

const getType = (id) => {
  return filters.value[0].values.find((e) => e._id == id)?.name
}

onMounted(async () => {
  if (api.filters) {
    const qs = new URLSearchParams(window.location.search)

    filters.value = await API.get(api.filters + '?' + qs)
    const initial = Object.assign({}, initialValue)

    filters.value.forEach((e) => {
      const q = qs.get(e.key)
      initial[e.key] = q ? q.split(',') : []
    })

    if (qs.get('view')) initial.view = qs.get('view')
    if (qs.get('favorites') != null) initial.favorites = qs.get('favorites')

    model.value = Object.assign(model.value, initial)

    watch(
      () => model.value.favorites,
      async () => {
        filters.value = await API.get(api.filters + API.getQS(model.value))
      }
    )
  }
})
</script>

<template>
  <div id="vue-index-filters" class="mt-7" v-if="filters">
    <div
      class="columns is-mobile is-multiline is-variable is-1-mobile is-2-desktop"
    >
      <div class="column is-12-mobile is-6-tablet is-narrow-desktop">
        <button
          type="button"
          class="button is-black is-rounded is-uppercase is-fullwidth"
          @click="active.modal = true"
        >
          <span class="mr-2">{{ t('filters.types') }}</span>
          <span
            class="tag is-small is-primary is-rounded mr-2"
            v-text="model.type.length"
            v-if="model.type.length"
          />
          <b-icon icon="chevron-down" class="ml-auto"></b-icon>
        </button>
      </div>

      <div class="column is-12-mobile is-6-tablet is-narrow-desktop">
        <div class="buttons is-full-touch">
          <button
            type="button"
            class="button is-rounded is-uppercase"
            :class="[model.date == item ? 'is-primary' : 'is-black']"
            @click="setDate(item)"
            v-for="item in filters[1].values"
          >
            <span>{{ formatDay(item) }}</span>
            <b-icon icon="close" v-if="model.date == item"></b-icon>
          </button>
        </div>
      </div>

      <div class="column is-12-mobile is-6-tablet is-narrow-desktop ml-auto">
        <div class="field has-buttons">
          <div class="control">
            <button
              type="button"
              class="button is-rounded is-uppercase"
              :class="[model.view == 'index' ? 'is-white' : 'is-black']"
              @click="model.view = 'index'"
            >
              <span class="icon"><b-icon icon="list" /></span>
              <span>{{ t('filters.list') }}</span>
            </button>
          </div>

          <div class="control">
            <button
              type="button"
              class="button is-rounded is-uppercase"
              :class="[model.view == 'map' ? 'is-white' : 'is-black']"
              @click="model.view = 'map'"
            >
              <span class="icon"><b-icon icon="map-1" /></span>
              <span>{{ t('filters.map') }}</span>
            </button>
          </div>
        </div>
      </div>
      <div class="column is-12-mobile is-6-tablet is-narrow-desktop">
        <div class="field has-buttons">
          <div class="control">
            <button
              type="button"
              class="button is-rounded is-uppercase"
              :class="[model.favorites == '0' ? 'is-primary' : 'is-black']"
              @click="model.favorites = '0'"
            >
              <span>{{ t('filters.all') }}</span>
            </button>
          </div>

          <div class="control">
            <button
              type="button"
              class="button is-rounded is-uppercase"
              :class="[model.favorites == '1' ? 'is-primary' : 'is-black']"
              @click="model.favorites = '1'"
            >
              <span class="icon"><b-icon icon="star" /></span>
              <span>{{ t('filters.fav') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="buttons mt-4" v-if="model.type.length">
      <button
        type="button"
        class="button is-small is-primary"
        v-for="(item, index) in model.type"
        @click="removeType(index)"
      >
        <span>{{ getType(item) }}</span>
        <b-icon icon="close"></b-icon>
      </button>

      <button
        type="button"
        class="button is-small is-secondary is-underlined"
        @click="resetModel"
      >
        <span>{{ t('filters.reset') }}</span>
      </button>
    </div>

    <IndexFiltersModal
      v-model:active="active.modal"
      v-model:model="model.type"
      :values="filters[0].values"
    />
  </div>
</template>
